import React, { useCallback, useEffect, useRef, useState } from 'react';

const SWIPE_THRESHOLD = 44;

/**
 * 카드뉴스 본문: 제목 → 이미지 캐러셀 → 점.
 * 이미지 위 스와이프·점·좌우 화살표 키는 슬라이드만 넘긴다(공지 간 이동은 팝업 탭이 맡는다).
 * 공지가 바뀌면 부모가 key로 새로 마운트하므로 슬라이드 위치는 따로 초기화하지 않는다.
 */
const CardNewsBody = ({ announcement, showArrows }) => {
  const slides = announcement.slides || [];
  const slideCount = slides.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const gestureRef = useRef(null);

  const moveSlide = useCallback((direction) => {
    if (!slideCount) return;
    setActiveIndex(previous => (previous + direction + slideCount) % slideCount);
  }, [slideCount]);

  // 탭 목록이 먼저 처리한 화살표 키(defaultPrevented)는 건너뛴다.
  useEffect(() => {
    if (slideCount < 2) return undefined;
    const handleKeyDown = (event) => {
      if (event.defaultPrevented) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveSlide(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveSlide(1);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [moveSlide, slideCount]);

  const handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target.closest('button')) return;
    gestureRef.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerUp = (event) => {
    const gesture = gestureRef.current;
    gestureRef.current = null;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - gesture.startX;
    const deltaY = Math.abs(event.clientY - gesture.startY);
    if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) <= deltaY) return;
    moveSlide(deltaX < 0 ? 1 : -1);
  };

  return (
    <div className="announcement-cardnews">
      <div className="announcement-modal__head">
        {announcement.badge && (
          <span className="announcement-modal__badge">{announcement.badge}</span>
        )}
        <p className="announcement-modal__title announcement-modal__title--compact">{announcement.title}</p>
      </div>

      <div
        aria-label={`${announcement.title} 슬라이드`}
        aria-roledescription="carousel"
        className="cardnews-carousel"
        onPointerCancel={() => { gestureRef.current = null; }}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        role="region"
      >
        <div
          className="cardnews-carousel-track"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {slides.map((slide, index) => (
            <div
              aria-hidden={index !== activeIndex}
              aria-label={`${index + 1} / ${slideCount}`}
              className="cardnews-carousel-slide"
              key={slide.image}
              role="group"
            >
              <img
                alt={slide.alt}
                decoding="async"
                draggable="false"
                height="840"
                loading={index === 0 ? 'eager' : 'lazy'}
                src={slide.image}
                width="840"
              />
            </div>
          ))}
        </div>

        {showArrows && slideCount > 1 && (
          <>
            <button
              aria-label="이전 슬라이드"
              className="cardnews-carousel-arrow cardnews-carousel-arrow--previous"
              onClick={() => moveSlide(-1)}
              type="button"
            >
              <svg aria-hidden="true" fill="none" height="22" viewBox="0 0 24 24" width="22">
                <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
              </svg>
            </button>
            <button
              aria-label="다음 슬라이드"
              className="cardnews-carousel-arrow cardnews-carousel-arrow--next"
              onClick={() => moveSlide(1)}
              type="button"
            >
              <svg aria-hidden="true" fill="none" height="22" viewBox="0 0 24 24" width="22">
                <path d="M9 5l7 7-7 7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
              </svg>
            </button>
          </>
        )}

        {slideCount > 1 && (
          <span aria-live="polite" className="cardnews-carousel-count">
            {activeIndex + 1} / {slideCount}
          </span>
        )}
      </div>

      {slideCount > 1 && (
        <div aria-label="슬라이드 선택" className="cardnews-carousel-dots" role="group">
          {slides.map((slide, index) => (
            <button
              aria-current={index === activeIndex ? 'true' : undefined}
              aria-label={`${index + 1}번 슬라이드 보기`}
              className={`cardnews-carousel-dot${index === activeIndex ? ' cardnews-carousel-dot--active' : ''}`}
              key={slide.image}
              onClick={() => setActiveIndex(index)}
              type="button"
            >
              <span />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CardNewsBody;
